/**
 * gra.ts — Categoría "Granos" (10 productos demo).
 * Insumos base usados por recetas de restaurante, fast food y panadería.
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_gra",
  skuPrefix: "GRA",
  categoria: "Granos",
} as const;

export const cat_gra_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Arroz Blanco Diana 500g",
    marca: "Diana",
    modelo: "Premium",
    origen: "Colombia",
    costo: 2100,
    precio: 3200,
    stock: 180,
    stockMinimo: 40,
    stockMaximo: 300,
    unidad: "bolsa",
    ubicacionFisica: "Estante A1, granos secos",
    proveedor: "Molinos Roa S.A.",
    tiempoEntrega: "3 días",
    vencimientoMeses: 12,
    pesoNeto: "500g",
    descripcionLarga:
      "Arroz blanco de grano largo, seleccionado y pulido para una cocción pareja y granos sueltos. Ideal para acompañar carnes, pescados y guisos del día a día.\n\nEmpaque sellado que preserva frescura. Rinde en promedio 4 porciones por bolsa.",
    ingredientes: "100% arroz blanco",
    especificaciones: [
      { clave: "Tipo", valor: "Grano largo" },
      { clave: "Cocción", valor: "18 min" },
      { clave: "Presentación", valor: "Bolsa 500g" },
    ],
    imageKeywords: ["rice", "arroz", "white-rice"],
    color: "bg-amber-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Fríjol Rojo 500g",
    marca: "La Fazenda",
    origen: "Colombia",
    costo: 4200,
    precio: 6500,
    stock: 120,
    stockMinimo: 25,
    stockMaximo: 220,
    unidad: "bolsa",
    ubicacionFisica: "Estante A1, granos secos",
    proveedor: "Distribuidora Andina de Granos",
    vencimientoMeses: 18,
    pesoNeto: "500g",
    descripcionLarga:
      "Fríjol rojo colombiano seleccionado, base insustituible de la bandeja paisa y preparaciones tradicionales. Grano uniforme, sin impurezas.\n\nRinde 5-6 porciones. Remojar mínimo 4 horas antes de cocción.",
    especificaciones: [
      { clave: "Variedad", valor: "Rojo Cargamanto" },
      { clave: "Cocción", valor: "45-60 min" },
    ],
    imageKeywords: ["red-beans", "frijol", "legume"],
    color: "bg-red-700",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Lenteja Pardina 500g",
    marca: "Doña Torcuata",
    origen: "Canadá",
    costo: 3800,
    precio: 5900,
    stock: 95,
    stockMinimo: 20,
    stockMaximo: 180,
    unidad: "bolsa",
    ubicacionFisica: "Estante A1, granos secos",
    proveedor: "Distribuidora Andina de Granos",
    vencimientoMeses: 18,
    pesoNeto: "500g",
    descripcionLarga:
      "Lenteja seca de cocción rápida, rica en proteína vegetal y hierro. Perfecta para sopas, ensaladas tibias y guisos caseros.",
    especificaciones: [
      { clave: "Variedad", valor: "Pardina" },
      { clave: "Cocción", valor: "30 min" },
    ],
    imageKeywords: ["lentils", "legumbre", "beans"],
    color: "bg-amber-700",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Harina de Trigo Haz de Oros 1kg",
    marca: "Haz de Oros",
    modelo: "Todo Uso",
    origen: "Colombia",
    costo: 4500,
    precio: 6800,
    stock: 140,
    stockMinimo: 30,
    stockMaximo: 260,
    unidad: "kg",
    ubicacionFisica: "Estante A2, harinas",
    proveedor: "Molinos Roa S.A.",
    vencimientoMeses: 10,
    pesoNeto: "1kg",
    descripcionLarga:
      "Harina de trigo refinada, ideal para pan, pastelería, empanadas y fritos. Consistencia fina y color uniforme.\n\nRinde aproximadamente 20 empanadas o 2 barras de pan.",
    especificaciones: [
      { clave: "Tipo", valor: "0000 todo uso" },
      { clave: "Proteína", valor: "10-11%" },
    ],
    imageKeywords: ["wheat-flour", "harina", "baking"],
    color: "bg-stone-200",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Harina de Maíz Precocida 1kg",
    marca: "PAN",
    modelo: "Blanca",
    origen: "Venezuela",
    costo: 5200,
    precio: 7900,
    stock: 110,
    stockMinimo: 25,
    stockMaximo: 200,
    unidad: "kg",
    ubicacionFisica: "Estante A2, harinas",
    proveedor: "Alimentos Polar",
    vencimientoMeses: 10,
    pesoNeto: "1kg",
    descripcionLarga:
      "Harina de maíz blanco precocida, ideal para arepas, bollos y hallacas. Lista para mezclar con agua tibia y sal.\n\nRinde aproximadamente 12-14 arepas por kg.",
    especificaciones: [
      { clave: "Variedad", valor: "Maíz blanco" },
      { clave: "Preparación", valor: "Agua tibia + sal" },
    ],
    imageKeywords: ["corn-flour", "masa-harina", "arepa"],
    color: "bg-yellow-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Azúcar Blanca Manuelita 1kg",
    marca: "Manuelita",
    origen: "Colombia",
    costo: 3600,
    precio: 5200,
    stock: 160,
    stockMinimo: 35,
    stockMaximo: 280,
    unidad: "kg",
    ubicacionFisica: "Estante A3, endulzantes",
    proveedor: "Ingenio Manuelita",
    vencimientoMeses: 24,
    pesoNeto: "1kg",
    descripcionLarga:
      "Azúcar blanca refinada de caña, grano uniforme de rápida disolución. Indispensable para repostería, bebidas calientes y frías.",
    especificaciones: [
      { clave: "Tipo", valor: "Refinada" },
      { clave: "Origen", valor: "Valle del Cauca" },
    ],
    imageKeywords: ["sugar", "azucar", "white-sugar"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Maíz Amarillo Trillado 500g",
    marca: "Doria",
    origen: "Colombia",
    costo: 2900,
    precio: 4500,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 160,
    unidad: "bolsa",
    ubicacionFisica: "Estante A1, granos secos",
    proveedor: "Distribuidora Andina de Granos",
    vencimientoMeses: 12,
    pesoNeto: "500g",
    descripcionLarga:
      "Maíz amarillo trillado para preparar mazamorra, sopas, coladas y tamales. Grano limpio y de excelente rendimiento.",
    especificaciones: [
      { clave: "Variedad", valor: "Amarillo" },
      { clave: "Cocción", valor: "60-90 min" },
    ],
    imageKeywords: ["corn", "yellow-corn", "maize"],
    color: "bg-yellow-500",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Avena en Hojuelas Quaker 500g",
    marca: "Quaker",
    modelo: "Tradicional",
    origen: "Perú",
    costo: 4800,
    precio: 7200,
    stock: 90,
    stockMinimo: 20,
    stockMaximo: 170,
    unidad: "bolsa",
    ubicacionFisica: "Estante A2, cereales",
    proveedor: "PepsiCo Alimentos",
    vencimientoMeses: 12,
    pesoNeto: "500g",
    tieneIVA: true,
    porcentajeIVA: 5,
    descripcionLarga:
      "Avena en hojuelas 100% integral, fuente de fibra y energía. Perfecta para desayunos, batidos, galletas y coladas.",
    especificaciones: [
      { clave: "Tipo", valor: "Hojuelas tradicionales" },
      { clave: "Fibra", valor: "4g por porción" },
    ],
    imageKeywords: ["oatmeal", "oats", "avena"],
    color: "bg-amber-100",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Garbanzo Seco 500g",
    marca: "La Fazenda",
    origen: "México",
    costo: 5500,
    precio: 8500,
    stock: 70,
    stockMinimo: 15,
    stockMaximo: 130,
    unidad: "bolsa",
    ubicacionFisica: "Estante A1, granos secos",
    proveedor: "Distribuidora Andina de Granos",
    vencimientoMeses: 18,
    pesoNeto: "500g",
    descripcionLarga:
      "Garbanzo seco de tamaño grande, ideal para hummus, guisos y ensaladas. Rico en proteína y fibra.",
    especificaciones: [
      { clave: "Remojo", valor: "8-12 horas" },
      { clave: "Cocción", valor: "90 min" },
    ],
    imageKeywords: ["chickpeas", "garbanzo", "hummus"],
    color: "bg-amber-300",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Pasta Espagueti Doria 500g",
    marca: "Doria",
    modelo: "Espagueti No.5",
    origen: "Colombia",
    costo: 2800,
    precio: 4300,
    stock: 200,
    stockMinimo: 40,
    stockMaximo: 360,
    unidad: "bolsa",
    ubicacionFisica: "Estante A2, pastas",
    proveedor: "Pastas Doria S.A.",
    vencimientoMeses: 24,
    pesoNeto: "500g",
    descripcionLarga:
      "Espagueti de trigo durum, textura al dente y cocción uniforme. Base universal para platos italianos y caseros.",
    especificaciones: [
      { clave: "Tipo", valor: "Espagueti No.5" },
      { clave: "Cocción", valor: "8-10 min" },
    ],
    imageKeywords: ["spaghetti", "pasta", "italian"],
    color: "bg-yellow-200",
    shape: "square",
  }),
];
